import React, { useEffect, useState, forwardRef } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MaterialTable, { serialNumberColumn } from "../components/MaterialTableAdapter";
import { toast } from "react-toastify";
import { CalendarDays, FileDown, FileSpreadsheet, HandCoins, IndianRupee, Plus, ReceiptText } from "lucide-react";

import Button from "../components/Button";
import Loading from "../components/Loading";
import Select from "../components/Select";
import Input from "../components/Input";
import { Autocomplete, TextField } from "@mui/material";
// Material Table Icons
import AddBox from "@mui/icons-material/AddBox";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import Check from "@mui/icons-material/Check";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Clear from "@mui/icons-material/Clear";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Edit from "@mui/icons-material/Edit";
import FilterList from "@mui/icons-material/FilterList";
import FirstPage from "@mui/icons-material/FirstPage";
import LastPage from "@mui/icons-material/LastPage";
import Remove from "@mui/icons-material/Remove";
import SaveAlt from "@mui/icons-material/SaveAlt";
import Search from "@mui/icons-material/Search";
import ViewColumn from "@mui/icons-material/ViewColumn";
import { exportToExcel, exportToPDF } from "../utils/exportUtils";
import { displayTransactionId } from "../utils/transactionIdUtils";

import {
  getAllTransactions,
  createAdminTransaction,
  getUsersForTransaction,
  getUserBalanceById,
  markDepositAsPaid,
  getWalletSummary,
  getSpendTransactions,
  createAdminSpend,
  getPlayerTableTime,
  getTableBills,
} from "../actions/walletActions";
import { SpendInvoiceDialog } from "../components/TableInvoice";
import TableBillSettleDialog, { SpendBillSettleDialog } from "../components/TableBillSettleDialog";
import { listClubs } from "../actions/clubActions";
import {
  CREATE_ADMIN_TRANSACTION_RESET,
  GET_USERS_FOR_TRANSACTION_RESET,
} from "../constants/walletConstants";
import { useDialog } from '../components/DialogProvider';
import ScreenHeader from '../components/layout/ScreenHeader'
import { Button as UIButton, FilterCard, FilterToggle, TextInput, Tabs } from "../ui";

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const BONUS_TRANSACTION_TYPES = ["deposit"];
const WALLET_BALANCE_TRANSACTION_TYPES = ["cashout"];

// Spend tab: what players have settled, grouped by category.
// `types` are the wallet transactionType values that belong to each category.
// Only table time for now: the other categories (and their rows) are hidden.
const SPEND_CATEGORIES = [
  // { key: "food-drink", label: "Food & drink", types: ["food-drink"] },
  { key: "table-time", label: "Table charge", types: ["table-charge"] },
  // { key: "membership", label: "Membership", types: ["membership"] },
  // { key: "event-entry", label: "Event entry", types: ["event-entry"] },
];
const SPEND_TYPES = SPEND_CATEGORIES.flatMap((category) => category.types);
const TABLE_TIME_KEY = "table-time";

const formatMinutes = (minutes) => {
  const total = Math.max(0, Math.round(minutes || 0));
  const hours = Math.floor(total / 60);
  return hours ? `${hours}h ${total % 60}m` : `${total % 60}m`;
};

const getSpendCategory = (transaction) =>
  SPEND_CATEGORIES.find((category) =>
    category.types.includes(transaction?.transactionType),
  );

const spendLabelOf = (transaction) =>
  getSpendCategory(transaction)?.label || transaction?.transactionType;

// Unpaid spend bill: added while the wallet could not cover it, not settled yet
const isUnpaidSpend = (transaction) =>
  transaction?.status === "pending" && transaction?.paymentStatus === "unpaid";

// One payment = the wallet rows paid together: one Add Spend or one bill settlement
// share a referenceId. Older rows without one are grouped when the same player paid
// at the same club in the same second (e.g. several table bills settled at once).
const spendPaymentKey = (txn) =>
  txn.referenceId ||
  [
    txn.userId?._id || txn.userId,
    txn.clubId?._id || txn.clubId,
    Math.floor(new Date(txn.createdAt).getTime() / 1000),
  ].join("|");

// Spend rows -> one table row per payment, newest first. `creditDues`: wallet row
// id -> amount still owed on table time charged to the wallet on credit
const groupSpendPayments = (transactions, creditDues = {}) => {
  const groups = new Map();
  transactions.forEach((txn) => {
    const key = spendPaymentKey(txn);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(txn);
  });

  return Array.from(groups, ([key, items]) => {
    const sorted = [...items].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    const latest = sorted[sorted.length - 1];
    const methods = [...new Set(sorted.filter((t) => !isUnpaidSpend(t)).map((t) => t.paymentMethod))];
    return {
      _id: key,
      paymentKey: key,
      items: sorted,
      userId: latest.userId,
      clubId: latest.clubId,
      clubName: latest.clubName,
      createdAt: latest.createdAt,
      amount: sorted.reduce((sum, t) => sum + getNumericValue(t.amount), 0),
      categories: [...new Set(sorted.map(spendLabelOf))],
      description: sorted.map((t) => t.description).filter(Boolean).join(", "),
      transactionId: displayTransactionId(latest),
      unpaid: sorted.some(isUnpaidSpend),
      // Charged to the wallet on credit ("mark as unpaid") and not yet cleared by a top-up
      creditDue: sorted.reduce((sum, t) => sum + getNumericValue(creditDues[t._id]), 0),
      // Only cash is named on the badge; account payments just read "Paid"
      paidWith: methods.includes("cash") ? "Cash" : "",
    };
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

// Table bills with no wallet row of their own (unpaid, or paid in cash at the desk)
// -> Spend rows: one per unpaid bill, one per cash collection
const groupTableBills = (bills) => {
  const groups = new Map();
  bills.forEach((bill) => {
    const unpaid = bill.paymentStatus === "unpaid";
    const key = [
      "bills",
      unpaid ? "unpaid" : "cash",
      bill.player?._id,
      bill.club?._id,
      unpaid ? bill._id : Math.floor(new Date(bill.paidAt).getTime() / 1000),
    ].join("|");
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(bill);
  });

  return Array.from(groups, ([key, bills]) => {
    const unpaid = bills[0].paymentStatus === "unpaid";
    // Invoice items, shaped like the wallet rows SpendInvoiceDialog expects
    const items = [...bills]
      .sort((a, b) => new Date(a.endedAt) - new Date(b.endedAt))
      .map((bill) => ({
        _id: bill._id,
        transactionType: "table-charge",
        amount: getNumericValue(bill.due),
        description:
          `Table charge - ${bill.table?.tableName ? `${bill.table.tableName}, ` : ""}seat ${bill.seatNumber}, ` +
          `${(bill.billableMinutes || 0) / 60}h @ Rs ${bill.hourlyRate}/hr` +
          (bill.gstAmount > 0 ? ` + Rs ${bill.gstAmount} GST` : ""),
        createdAt: unpaid ? bill.endedAt : bill.paidAt,
        userId: bill.player,
        clubId: bill.club,
        clubName: bill.club?.name,
        transactionId: String(parseInt(String(bill._id).slice(-8), 16)),
        paymentMethod: "cash",
        tableBill: {
          tableName: bill.table?.tableName || null,
          tableNumber: bill.table?.tableNumber || null,
          seatNumber: bill.seatNumber,
          startedAt: bill.startedAt,
          endedAt: bill.endedAt,
          billableMinutes: bill.billableMinutes,
          hourlyRate: bill.hourlyRate,
          gstRate: bill.gstRate || 0,
          gstAmount: bill.gstAmount || 0,
        },
      }));
    const latest = items[items.length - 1];
    return {
      _id: key,
      paymentKey: key,
      tableBills: true,
      items,
      userId: bills[0].player,
      clubId: bills[0].club,
      clubName: bills[0].club?.name,
      createdAt: latest.createdAt,
      amount: items.reduce((sum, item) => sum + item.amount, 0),
      categories: ["Table charge"],
      description: items.map((item) => item.description).join(", "),
      transactionId: "—",
      unpaid,
      // Cashier pressed "Mark as unpaid" on these bills
      markedUnpaid: unpaid && bills.some((bill) => bill.markedUnpaidAt),
      paidWith: "Cash",
    };
  });
};

const TRANSACTION_TABS = [
  { value: "topups", label: "Top-ups" },
  { value: "spend", label: "Spend" },
];

// Player search field styling (shared by the transaction and spend modals)
const playerSelectSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'var(--cm-surface-2)',
    color: 'var(--cm-text)',
    borderRadius: '8px',
    '& fieldset': {
      borderColor: 'var(--cm-border-strong)',
    },
    '&:hover fieldset': {
      borderColor: 'var(--cm-text-muted)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--cm-gold)',
      borderWidth: '1px',
    }
  },
  '& .MuiInputLabel-root': { color: 'var(--cm-text-muted)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--cm-gold-text)' },
  '& .MuiInputLabel-shrink': { backgroundColor: 'var(--cm-surface)', px: 0.5, borderRadius: '4px' },
  '& .MuiSvgIcon-root': { color: 'var(--cm-text-muted)' },
};

const getNumericValue = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getTransactionDisplayName = (transaction) => {
  if (transaction?.transactionType === "deposit") {
    return "Top-up into Account";
  }
  if (transaction?.transactionType === "cashout") {
    return "Cashout from Wallet";
  }
  if (transaction?.transactionType === "table-charge") {
    return "Table Charge from Wallet";
  }

  const transactionType = transaction?.transactionType || "";
  return transactionType
    ? transactionType.charAt(0).toUpperCase() + transactionType.slice(1)
    : "N/A";
};

const AdminTransactions = () => {
  const { confirm } = useDialog();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // States
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]); // State for filtered data
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [transactionType, setTransactionType] = useState("deposit");
  const [amount, setAmount] = useState("");
  const [bonusAmount, setBonusAmount] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [usersData, setUsersData] = useState([]); // Store full user data including assignedClubs
  const [selectedUserBalance, setSelectedUserBalance] = useState(0);
  const [financialSummary, setFinancialSummary] = useState({
    walletBalance: 0,
    totalDeposits: 0,
    totalCashouts: 0,
    pendingCount: 0,
  });

  // Spend tab
  const [activeTab, setActiveTab] = useState("topups");
  const [spendData, setSpendData] = useState([]);
  const [spendLoading, setSpendLoading] = useState(false);
  const [spendCategory, setSpendCategory] = useState(null);
  // Table bills not in the wallet: unpaid ones and ones paid in cash
  const [tableBills, setTableBills] = useState([]);
  // Wallet row id -> amount still owed on table time charged on credit
  const [creditDues, setCreditDues] = useState({});
  // Spend tab: show only what players still owe
  const [unpaidOnly, setUnpaidOnly] = useState(false);
  // Player whose unpaid table bills are being settled
  const [settleBillsPlayer, setSettleBillsPlayer] = useState(null);

  // Date filter for both tabs ("" = no limit), as YYYY-MM-DD
  const [dateFrom, setDateFrom] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dateTo, setDateTo] = useState("");
  const dateRange = {
    startDate: dateFrom ? moment(dateFrom).startOf("day").toISOString() : "",
    endDate: dateTo ? moment(dateTo).endOf("day").toISOString() : "",
  };

  // Add Spend modal
  const [openSpendModal, setOpenSpendModal] = useState(false);
  const [spendTypes, setSpendTypes] = useState([]);
  const [spendAmounts, setSpendAmounts] = useState({}); // category key -> amount
  const [spendRemarks, setSpendRemarks] = useState("");
  const [savingSpend, setSavingSpend] = useState(false);
  // How the spend is paid ("" = pick automatically) and cash added to the wallet first
  const [spendPaymentMode, setSpendPaymentMode] = useState("");
  const [spendDepositAmount, setSpendDepositAmount] = useState("");
  // Selected player's table time at the selected club: { stays, totalMinutes, totalDue }
  const [tableTime, setTableTime] = useState(null);
  const [tableTimeLoading, setTableTimeLoading] = useState(false);

  // Spend invoice: the wallet rows of the spend being invoiced
  const [invoiceTransactions, setInvoiceTransactions] = useState(null);
  // Player whose unpaid spends are being settled
  const [settlePlayer, setSettlePlayer] = useState(null);

  // Club Selection States (for super admins or multi-club admins)
  const [selectedClub, setSelectedClub] = useState("");
  const [clubOptions, setClubOptions] = useState([]);
  const [showClubDropdown, setShowClubDropdown] = useState(false);

  // Remarks State (mandatory)
  const [remarks, setRemarks] = useState("");


  // Cashout Mode (online/cash)
  const [cashoutMode, setCashoutMode] = useState("");

  // Check for auth (admin login) - with proper null checking
  const adminLogin = useSelector((state) => state.adminLogin) || {};
  const { userInfo: adminInfo } = adminLogin;

  // Fallback to userLogin if adminLogin not available
  const userLogin = useSelector((state) => state.userLogin) || {};
  const { userInfo } = userLogin;

  // Determine if user is super admin or has multiple clubs
  // Use adminInfo first, fallback to userInfo
  const currentUser = adminInfo || userInfo;
  const isSuperAdmin = currentUser?.role?.isSuperAdmin === true;
  // Transaction Management permissions from the role (mirrors backend checks)
  const hasTxnPermission = (permission) =>
    isSuperAdmin || currentUser?.role?.[permission] === true;
  const canAddTransactions = hasTxnPermission("transactionsAdd");
  const canUpdateTransactions = hasTxnPermission("transactionsUpdate");
  const canSettleBills = canAddTransactions || canUpdateTransactions;
  const assignedClubs = currentUser?.assignedClubs || [];
  // Staff (cashier, manager, ...) work only within their assigned clubs; with a
  // single club it is used automatically
  const isSingleClubStaff = !isSuperAdmin && assignedClubs.length === 1;
  const singleAssignedClub = isSingleClubStaff ? assignedClubs[0] : null;
  const singleAssignedClubId = singleAssignedClub
    ? String(singleAssignedClub._id || singleAssignedClub.id || singleAssignedClub)
    : "";
  const needsClubSelection = true;
  const numericAmount = getNumericValue(amount);
  const numericBonusAmount = BONUS_TRANSACTION_TYPES.includes(transactionType)
    ? getNumericValue(bonusAmount)
    : 0;
  const handleAmountChange = (e) => {
    setAmount(e.target.value);
  };

  const handleTransactionTypeChange = (e) => {
    const newType = e.target.value;
    setTransactionType(newType);

    // Reset payment status and cashout mode when transaction type changes
    setCashoutMode("");
    setBonusAmount("");

  };

  const handleBonusAmountChange = (e) => {
    setBonusAmount(e.target.value);
  };

  const handleUserSelection = async (userId) => {
    setSelectedUser(userId);
    setSelectedClub(isSingleClubStaff ? singleAssignedClubId : "");

    if (userId) {
      try {
        const balanceData = await dispatch(getUserBalanceById(userId));
        if (balanceData && balanceData.success) {
          setSelectedUserBalance(balanceData.balance);
        }
      } catch (error) {
        console.error("Error fetching user balance:", error);
        setSelectedUserBalance(0);
      }

      // Don't restrict clubs based on user assignment - allow Admin to select any accessible club
      // The clubOptions are already set by the useEffect based on Admin's permissions
    } else {
      setSelectedUserBalance(0);
    }
  };

  // Fetch the Top-ups tab rows (wallet top-ups and cashouts) in the date range
  const fetchTopups = () =>
    dispatch(getAllTransactions(1, 1000, "deposit,cashout", "", "", dateRange));

  // Fetch spend (wallet rows and table bills) for the Spend tab in the date range
  const fetchSpend = async () => {
    setSpendLoading(true);
    const [res, billsRes] = await Promise.all([
      dispatch(getSpendTransactions(SPEND_TYPES, 1000, dateRange)),
      dispatch(getTableBills(dateRange)),
    ]);
    setSpendData(
      (res?.transactions || []).filter(
        (txn) => !["rejected", "cancelled"].includes(txn.status),
      ),
    );
    setTableBills(billsRes?.bills || []);
    setCreditDues(billsRes?.creditDues || {});
    setSpendLoading(false);
  };

  // Fetch clubs for dropdown (needed for super admins or multi-club admins)
  const clubList = useSelector((state) => state.clubList);
  const { clubs = [] } = clubList || {};

  // Initial data fetch - runs once on mount
  useEffect(() => {
    dispatch(getUsersForTransaction());

    // Fetch all clubs for any admin
    dispatch(listClubs({ isActive: true }));

    // Fetch Financial Summary
    const fetchSummary = async () => {
      const res = await dispatch(getWalletSummary(isSuperAdmin ? {} : {}));
      if (res && res.success) {
        setFinancialSummary(res.summary);
      }
    };
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isSuperAdmin]);

  // Both tabs reload whenever the date range changes
  useEffect(() => {
    fetchTopups();
    fetchSpend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, dateFrom, dateTo]);

  // Single-club cashiers use their assigned club automatically. Everyone
  // else can select from the clubs available to them.
  useEffect(() => {
    setShowClubDropdown(!isSingleClubStaff);
    if (isSingleClubStaff && singleAssignedClubId) {
      setSelectedClub(singleAssignedClubId);
    }
  }, [isSingleClubStaff, singleAssignedClubId]);

  // Super admins pick from every club; other staff only from their assigned clubs
  useEffect(() => {
    const assignedClubIds = assignedClubs.map((c) => String(c._id || c.id || c));
    const visibleClubs = isSuperAdmin
      ? clubs || []
      : (clubs || []).filter((c) => assignedClubIds.includes(String(c._id)));
    setClubOptions(
      visibleClubs.map((c) => ({
        id: c._id,
        title: `${c.clubCode} - ${c.name}`,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubs, isSuperAdmin]);

  // Add Spend: load the player's current table time once player and club are chosen
  useEffect(() => {
    if (!openSpendModal || !selectedUser || !selectedClub) {
      setTableTime(null);
      return undefined;
    }
    let cancelled = false;
    setTableTimeLoading(true);
    dispatch(getPlayerTableTime(selectedUser, selectedClub)).then((res) => {
      if (cancelled) return;
      setTableTimeLoading(false);
      if (!res?.success) {
        setTableTime(null);
        toast.error(res?.message || "Could not load table charges");
        return;
      }
      setTableTime(res.data);
      // Table time already picked: fill in what the player owes
      setSpendAmounts((amounts) =>
        spendTypes.some((type) => type.key === TABLE_TIME_KEY) && !amounts[TABLE_TIME_KEY] && res.data.totalDue > 0
          ? { ...amounts, [TABLE_TIME_KEY]: String(res.data.totalDue) }
          : amounts,
      );
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, openSpendModal, selectedUser, selectedClub]);

  // Get All Transactions
  const getAllTransactionsState = useSelector(
    (state) => state.getAllTransactions,
  );
  const {
    loadingGetAllTransactionsInfo,
    getAllTransactionsData,
    errorGetAllTransactionsInfo,
  } = getAllTransactionsState;

  useEffect(() => {
    if (getAllTransactionsData) {
      const walletCashTransactions = (
        getAllTransactionsData.transactions || []
      ).filter((transaction) =>
        ["deposit", "cashout"].includes(transaction.transactionType),
      );
      setLoading(false);
      setData(walletCashTransactions);
      setFilteredData(walletCashTransactions);
    } else if (errorGetAllTransactionsInfo) {
      setLoading(false);
      toast(errorGetAllTransactionsInfo, {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
    }
  }, [getAllTransactionsData, errorGetAllTransactionsInfo]);

  // Get Users for Transaction
  const getUsersForTransactionState = useSelector(
    (state) => state.getUsersForTransaction,
  );
  const { getUsersForTransactionData, errorGetUsersForTransaction } =
    getUsersForTransactionState;

  useEffect(() => {
    if (getUsersForTransactionData && getUsersForTransactionData.users) {
      // Store full user data including assignedClubs
      setUsersData(getUsersForTransactionData.users);
      const options = getUsersForTransactionData.users.map((user) => ({
        id: user._id,
        title: `${user.name} (${user.identifier})`,
      }));
      setUserOptions(options);
    } else if (errorGetUsersForTransaction) {
      toast(errorGetUsersForTransaction, {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
    }
  }, [getUsersForTransactionData, errorGetUsersForTransaction]);

  // Create Admin Transaction
  const createAdminTransactionState = useSelector(
    (state) => state.createAdminTransaction,
  );
  const {
    loadingCreateAdminTransaction,
    createAdminTransactionData,
    errorCreateAdminTransaction,
  } = createAdminTransactionState;

  useEffect(() => {
    dispatch({ type: CREATE_ADMIN_TRANSACTION_RESET });
    if (createAdminTransactionData && createAdminTransactionData.success) {
      toast("Transaction created successfully", {
        type: "success",
        hideProgressBar: true,
        autoClose: 2000,
      });
      closeAddTransactionModal();
      // Refresh transactions (a top-up may have settled table bills)
      fetchTopups();
      fetchSpend();
      // Refresh summary
      const fetchSummary = async () => {
        const res = await dispatch(getWalletSummary({}));
        if (res && res.success) {
          setFinancialSummary(res.summary);
        }
      };
      fetchSummary();
    } else if (errorCreateAdminTransaction) {
      toast(errorCreateAdminTransaction, {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
    }
  }, [createAdminTransactionData, errorCreateAdminTransaction, dispatch]);

  // * Export Handlers
  const prepareExportData = (dataToExport) => {
    return dataToExport.map((txn) => {
      const date = new Date(txn.createdAt);

      // Club Name
      let clubName = "N/A";
      if (txn.clubId && txn.clubId.clubCode) {
        clubName = `${txn.clubId.clubCode} - ${txn.clubId.name}`;
      } else if (txn.clubName) {
        clubName = txn.clubName;
      }

      return {
        "Transaction ID": displayTransactionId(txn),
        Name: txn.userId?.name || "N/A",
        Type: getTransactionDisplayName(txn),
        Amount: txn.amount, // Keep numeric for Excel
        Bonus: txn.bonusAmount || 0,
        Date: `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`,
        Status: txn.status.charAt(0).toUpperCase() + txn.status.slice(1),
        Club: clubName,
      };
    });
  };

  const handleExportExcel = () => {
    const exportData = prepareExportData(filteredData);
    const columns = [
      { title: "Date", field: "Date" },
      { title: "Transaction ID", field: "Transaction ID" },
      { title: "Name", field: "Name" },
      { title: "Type", field: "Type" },
      { title: "Amount", field: "Amount" },
      { title: "Bonus", field: "Bonus" },
      { title: "Status", field: "Status" },
      { title: "Club", field: "Club" },
    ];
    exportToExcel(exportData, columns, "Admin_Transactions");
  };

  const handleExportPDF = () => {
    const exportData = prepareExportData(filteredData);
    const columns = [
      { title: "Date", field: "Date" },
      { title: "Trans ID", field: "Transaction ID" },
      { title: "Name", field: "Name" },
      { title: "Type", field: "Type" },
      { title: "Amt", field: "Amount" }, // Shorten title for PDF
      { title: "Bonus", field: "Bonus" },
      { title: "Status", field: "Status" },
      { title: "Club", field: "Club" },
    ];
    exportToPDF(
      exportData,
      columns,
      "Admin_Transactions",
      "Transactions Report",
    );
  };

  // Transaction Type Options
  const transactionTypeOptions = [
    { id: "deposit", title: "Top-up into Account" },
  ];

  // Table Columns
  const headCells = [
    serialNumberColumn,
    {
      field: "userId",
      title: "Name",
      render: (rowData) => rowData.userId?.name || "N/A",
    },
    {
      field: "transactionType",
      title: "Transaction Type",
      render: (rowData) => {
        const displayName = getTransactionDisplayName(rowData);

        return (
          <div className="flex flex-col items-start space-y-1">
            <span
              className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                rowData.transactionType === "deposit"
                  ? "bg-green-100 text-green-800"
                  : rowData.transactionType === "cashout"
                  ? "bg-blue-100 text-blue-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {displayName}
            </span>
            {/* Only top-ups that are not simply approved get a status */}
            {rowData.status !== "approved" && (
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  rowData.status === "pending"
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {rowData.status === "pending" && rowData.paymentStatus === "unpaid"
                  ? "⏳ Unpaid"
                  : rowData.status.charAt(0).toUpperCase() + rowData.status.slice(1)}
              </span>
            )}
            {/* Show "Mark as Paid" button for unpaid pending deposits */}
            {canUpdateTransactions &&
              rowData.transactionType === "deposit" &&
              rowData.paymentStatus === "unpaid" &&
              rowData.status === "pending" && (
                <button
                  onClick={async () => {
                    if (
                      await confirm({
                        title: "Mark top-up as paid",
                        message:
                          "Are you sure you want to mark this top-up as PAID? This will update the user's wallet balance.",
                        confirmText: "Mark as paid",
                      })
                    ) {
                      const result = await dispatch(
                        markDepositAsPaid(rowData._id),
                      );
                      if (result?.success) {
                        toast.success(
                          "Top-up marked as paid! Wallet balance updated.",
                        );
                        fetchTopups();
                      } else {
                        toast.error(result?.error || "Failed to mark as paid");
                      }
                    }
                  }}
                  className="px-2 py-1 bg-cm-gold text-cm-gold-ink text-xs rounded hover:bg-cm-gold transition-colors font-medium"
                >
                  ✓ Mark as Paid
                </button>
              )}
            {/* Show cashout mode for cashouts */}
            {rowData.transactionType === "cashout" && rowData.cashoutMode && (
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  rowData.cashoutMode === "online"
                    ? "bg-blue-200 text-blue-800"
                    : "bg-emerald-200 text-emerald-800"
                }`}
              >
                {rowData.cashoutMode === "online" ? "🌐 Online" : "💵 Cash"}
              </span>
            )}
          </div>
        );
      },
    },
    {
      field: "amount",
      title: "Amount",
      render: (rowData) => {
        const rowAmount = getNumericValue(rowData.amount);
        const rowBonusAmount = getNumericValue(rowData.bonusAmount);
        const totalValue = rowAmount + rowBonusAmount;
        const showWalletCredit = rowData.transactionType === "deposit";
        // Money into the player's account reads green, money out red (as in the player app)
        const isCredit = ["deposit", "chip-deposit", "cashout"].includes(rowData.transactionType);

        return (
          <div className="flex flex-col leading-tight">
            <span
              className={`whitespace-nowrap font-semibold ${
                isCredit ? "text-cm-success" : "text-cm-danger"
              }`}
            >
              Rs {rowAmount.toLocaleString("en-IN")}
              {rowBonusAmount > 0 && (
                <span className="ml-1.5 text-xs font-medium text-emerald-700">
                  +Rs {rowBonusAmount.toLocaleString("en-IN")} bonus
                </span>
              )}
            </span>
            {showWalletCredit && (
              <span className="text-xs text-blue-700 whitespace-nowrap">
                Credit Rs {totalValue.toLocaleString("en-IN")}
              </span>
            )}
          </div>
        );
      },
    },
    {
      field: "createdAt",
      title: "Date Time",
      render: (rowData) => {
        const date = new Date(rowData.createdAt);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
      },
    },
    {
      field: "clubId",
      title: "Club",
      render: (rowData) => {
        if (rowData.clubId && rowData.clubId.clubCode) {
          return `${rowData.clubId.clubCode} - ${rowData.clubId.name}`;
        } else if (rowData.clubName) {
          return rowData.clubName;
        }
        return <span className="text-gray-400 italic">N/A</span>;
      },
    },
    {
      field: "transactionId",
      title: "Transaction ID",
      render: (rowData) => displayTransactionId(rowData),
    },
  ];

  // Spend tab: settled totals per category (unpaid bills apart) and the rows for the selected category
  // Table bills count as table time: unpaid ones as dues, cash ones as settled
  const tableBillRows = groupTableBills(tableBills);
  const sumOf = (rows) => rows.reduce((sum, row) => sum + getNumericValue(row.amount), 0);
  const creditDueOf = (txn) => getNumericValue(creditDues[txn._id]);
  const spendUnpaidTotal =
    sumOf(spendData.filter(isUnpaidSpend)) +
    sumOf(tableBillRows.filter((row) => row.unpaid)) +
    spendData.reduce((sum, txn) => sum + creditDueOf(txn), 0);
  // Settled = paid, less whatever is still owed on credit
  const spendTotals = spendData.reduce((totals, txn) => {
    const category = getSpendCategory(txn);
    if (category && !isUnpaidSpend(txn)) {
      totals[category.key] =
        (totals[category.key] || 0) + getNumericValue(txn.amount) - creditDueOf(txn);
    }
    return totals;
  }, {});
  spendTotals[TABLE_TIME_KEY] =
    (spendTotals[TABLE_TIME_KEY] || 0) + sumOf(tableBillRows.filter((row) => !row.unpaid));
  const spendTotal = Object.values(spendTotals).reduce((a, b) => a + b, 0);
  const visibleSpendData = [
    ...groupSpendPayments(
      spendCategory
        ? spendData.filter((txn) => getSpendCategory(txn)?.key === spendCategory)
        : spendData,
      creditDues,
    ),
    ...(!spendCategory || spendCategory === TABLE_TIME_KEY ? tableBillRows : []),
  ]
    .filter((row) => !unpaidOnly || row.unpaid || row.creditDue > 0)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const spendHeadCells = [
    serialNumberColumn,
    {
      field: "userId",
      title: "Name",
      render: (rowData) => rowData.userId?.name || "N/A",
    },
    {
      field: "categories",
      title: "Category",
      customSort: (a, b) => a.categories.join().localeCompare(b.categories.join()),
      render: (rowData) => (
        <div className="flex flex-wrap gap-1">
          {rowData.categories.map((label) => (
            <span
              key={label}
              className="inline-block px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 whitespace-nowrap"
            >
              {label}
            </span>
          ))}
        </div>
      ),
    },
    {
      field: "amount",
      title: "Amount",
      render: (rowData) => (
        <span className="whitespace-nowrap font-semibold text-cm-danger">
          Rs {getNumericValue(rowData.amount).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      field: "description",
      title: "Details",
      render: (rowData) =>
        rowData.items.length === 1 ? (
          rowData.items[0].description || "—"
        ) : (
          <ul className="m-0 p-0 list-none text-xs leading-5">
            {rowData.items.map((item) => (
              <li key={item._id} className="flex justify-between gap-3">
                <span>{item.description || spendLabelOf(item)}</span>
                <span className="tabular-nums text-cm-muted whitespace-nowrap">
                  Rs {getNumericValue(item.amount).toLocaleString("en-IN")}
                </span>
              </li>
            ))}
          </ul>
        ),
    },
    headCells.find((column) => column.field === "createdAt"),
    headCells.find((column) => column.field === "clubId"),
    {
      field: "transactionId",
      title: "Transaction ID",
    },
    {
      field: "unpaid",
      title: "Payment",
      render: (rowData) =>
        rowData.unpaid ? (
          <div className="flex flex-col items-start gap-1">
            <span
              className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                rowData.markedUnpaid
                  ? "bg-cm-warning-soft text-cm-warning"
                  : "bg-cm-danger-soft text-cm-danger"
              }`}
            >
              {rowData.markedUnpaid ? "Marked as unpaid" : "Unpaid"}
            </span>
            <span
              className={`text-xs font-semibold whitespace-nowrap ${
                rowData.markedUnpaid ? "text-cm-warning" : "text-cm-danger"
              }`}
            >
              ₹{getNumericValue(rowData.amount).toLocaleString("en-IN")} due
            </span>
          </div>
        ) : rowData.creditDue > 0 ? (
          <div className="flex flex-col items-start gap-1">
            <span className="px-2 py-1 rounded text-xs font-medium bg-cm-warning-soft text-cm-warning whitespace-nowrap">
              Unpaid · On wallet credit
            </span>
            <span className="text-xs font-semibold text-cm-warning whitespace-nowrap">
              ₹{rowData.creditDue.toLocaleString("en-IN")} due
            </span>
          </div>
        ) : (
          <span className="px-2 py-1 rounded text-xs font-medium bg-cm-success-soft text-cm-success whitespace-nowrap">
            {rowData.paidWith ? `Paid · ${rowData.paidWith}` : "Paid"}
          </span>
        ),
    },
    {
      field: "invoice",
      title: "Actions",
      sorting: false,
      render: (rowData) => (
        <div className="flex items-center gap-2">
          {canSettleBills && rowData.unpaid && rowData.userId?._id && (
            <button
              type="button"
              onClick={() =>
                (rowData.tableBills ? setSettleBillsPlayer : setSettlePlayer)(rowData.userId)
              }
              title={rowData.tableBills ? "Settle unpaid table bills" : "Settle unpaid spends"}
              aria-label={rowData.tableBills ? "Settle unpaid table bills" : "Settle unpaid spends"}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-cm-danger-soft text-cm-danger transition-colors hover:brightness-125 focus:outline-none focus:ring-2 focus:ring-cm-danger"
            >
              <IndianRupee size={16} />
            </button>
          )}
          <button
            type="button"
            onClick={() =>
              setInvoiceTransactions(rowData.tableBills ? rowData.items : getSpendGroup(rowData))
            }
            disabled={rowData.unpaid || rowData.creditDue > 0}
            title={rowData.unpaid || rowData.creditDue > 0 ? "Invoice is available once it is paid" : "Invoice"}
            aria-label="Invoice"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-cm-border-strong text-cm-muted transition-colors hover:border-cm-gold hover:text-cm-gold-text focus:outline-none focus:ring-2 focus:ring-cm-gold disabled:opacity-40 disabled:pointer-events-none"
          >
            <ReceiptText size={16} />
          </button>
        </div>
      ),
    },
  ];

  // Every item of a payment (ignoring the category filter) for its invoice
  const getSpendGroup = (row) =>
    spendData
      .filter((item) => spendPaymentKey(item) === row.paymentKey)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  // Add Spend: per-item amounts and the total debited from the wallet
  const spendItems = spendTypes.map((type) => ({
    category: type,
    amount: spendAmounts[type.key] ?? "",
  }));
  const spendTotalAmount = spendItems.reduce(
    (sum, item) => sum + getNumericValue(item.amount),
    0,
  );
  // Payment: wallet when it covers the spend, otherwise cash added to the wallet first
  // (cashier can switch to cash at the desk or unpaid). Table time cannot be unpaid:
  // it is already billed per stay.
  const walletCoversSpend = selectedUserBalance >= spendTotalAmount;
  const spendShortfall = Math.max(0, spendTotalAmount - selectedUserBalance);
  const hasTableTime = spendTypes.some((type) => type.key === TABLE_TIME_KEY);
  // A chosen option that no longer applies (wallet now short, table time added) falls back
  const chosenModeAvailable =
    !(spendPaymentMode === "wallet" && !walletCoversSpend) &&
    !(spendPaymentMode === "unpaid" && hasTableTime);
  const paymentMode =
    (chosenModeAvailable && spendPaymentMode) || (walletCoversSpend ? "wallet" : "deposit");
  const depositValue =
    spendDepositAmount === "" ? spendShortfall : getNumericValue(spendDepositAmount);
  const spendBalanceAfter =
    paymentMode === "wallet"
      ? selectedUserBalance - spendTotalAmount
      : paymentMode === "deposit"
        ? selectedUserBalance + depositValue - spendTotalAmount
        : selectedUserBalance;
  const SPEND_PAYMENT_OPTIONS = [
    {
      value: "wallet",
      label: "Debit from wallet",
      help: walletCoversSpend ? "Taken from the wallet balance" : "Insufficient wallet balance",
      disabled: !walletCoversSpend,
      action: "Debit from wallet",
    },
    {
      value: "cash",
      label: "Cash collected",
      help: "Paid at the desk; wallet untouched",
      action: "Cash collected · mark paid",
    },
    {
      value: "deposit",
      label: "Add cash to wallet & pay",
      help: "Cash credited, then the spend is debited; extra stays in the wallet",
      action: "Add to wallet & pay",
    },
    {
      value: "unpaid",
      label: "Add as unpaid",
      help: hasTableTime ? "Not for table charges (already billed per stay)" : "Settle later from the Spend tab",
      disabled: hasTableTime,
      action: "Add as unpaid",
    },
  ];

  const handleSpendTypesChange = (event, newValue) => {
    setSpendTypes(newValue);
    setSpendAmounts((amounts) => {
      const next = {};
      newValue.forEach((type) => {
        next[type.key] = amounts[type.key] ?? "";
      });
      // Table time defaults to what the player owes right now
      if (
        newValue.some((type) => type.key === TABLE_TIME_KEY) &&
        !next[TABLE_TIME_KEY] &&
        tableTime?.totalDue > 0
      ) {
        next[TABLE_TIME_KEY] = String(tableTime.totalDue);
      }
      return next;
    });
  };

  // Open Add Transaction Modal
  const openAddTransactionModal = () => {
    setOpenModal(true);
  };

  // ✅ Updated Close Add Transaction Modal
  const closeAddTransactionModal = () => {
    setOpenModal(false);
    setSelectedUser("");
    setTransactionType("deposit");
    setAmount("");
    setSelectedUserBalance(0);
    setRemarks("");
    setCashoutMode("");
    setBonusAmount("");

    if (isSingleClubStaff) {
      setSelectedClub(singleAssignedClubId);
    } else if (needsClubSelection) {
      setSelectedClub("");
    }
  };

  // Close Add Spend Modal
  const closeSpendModal = () => {
    setOpenSpendModal(false);
    setSelectedUser("");
    setSelectedUserBalance(0);
    setSpendTypes([]);
    setSpendAmounts({});
    setSpendRemarks("");
    setSpendPaymentMode("");
    setSpendDepositAmount("");
    setTableTime(null);
    setSelectedClub(isSingleClubStaff ? singleAssignedClubId : "");
  };

  // Add Spend Handler - debits the total of all items from the player's wallet
  const addSpendHandler = async () => {
    let validationError = "";
    if (!selectedUser || spendItems.length === 0) {
      validationError = "All fields are mandatory";
    } else if (!selectedClub) {
      validationError = "Please select a club";
    } else if (spendItems.some((item) => !(getNumericValue(item.amount) > 0))) {
      const missing = spendItems.find((item) => !(getNumericValue(item.amount) > 0));
      validationError = `Enter an amount for ${missing.category.label}`;
    } else if (paymentMode === "wallet" && !walletCoversSpend) {
      validationError = `Insufficient balance! Available: ₹${selectedUserBalance.toLocaleString("en-IN")}`;
    } else if (paymentMode === "deposit" && !(depositValue > 0)) {
      validationError = "Enter the cash amount to add to the wallet";
    } else if (paymentMode === "deposit" && selectedUserBalance + depositValue < spendTotalAmount) {
      validationError = `Add at least ₹${spendShortfall.toLocaleString("en-IN")} to cover the spend`;
    } else if (paymentMode === "unpaid" && hasTableTime) {
      validationError =
        "Table charge cannot be added unpaid. Settle it from the player's table bills.";
    }
    if (validationError) {
      toast(validationError, {
        type: "error",
        hideProgressBar: true,
        autoClose: 3000,
      });
      return;
    }

    setSavingSpend(true);
    const res = await dispatch(
      createAdminSpend({
        userId: selectedUser,
        items: spendItems.map((item) => ({
          transactionType: item.category.types[0],
          amount: getNumericValue(item.amount),
        })),
        clubId: selectedClub,
        adminNotes: spendRemarks.trim(),
        paymentMode,
        depositAmount: paymentMode === "deposit" ? depositValue : undefined,
      }),
    );
    setSavingSpend(false);

    if (res?.success) {
      toast.success(res.message || "Spend added");
      closeSpendModal();
      fetchSpend();
      const summaryRes = await dispatch(getWalletSummary({}));
      if (summaryRes && summaryRes.success) {
        setFinancialSummary(summaryRes.summary);
      }
    } else {
      toast.error(res?.message || "Failed to add spend");
    }
  };

  // ✅ FIXED: Updated Add Transaction Handler - includes club selection
  const addTransactionHandler = () => {
    if (!selectedUser || !transactionType || !amount) {
      toast("All fields are mandatory", {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      return;
    }

    // Validate remarks (mandatory for all transactions)
    if (!remarks || !remarks.trim()) {
      toast("Remarks is mandatory", {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      return;
    }

    // Validate cashout mode for Cashout
    if (transactionType === "cashout" && !cashoutMode) {
      toast("Please select cashout mode (Online/Cash)", {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      return;
    }

    // Validate club selection for super admins or multi-club admins
    if (needsClubSelection && !selectedClub) {
      toast("Please select a club", {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      return;
    }

    const amountValue = parseFloat(amount);
    const bonusValue = BONUS_TRANSACTION_TYPES.includes(transactionType)
      ? parseFloat(bonusAmount || 0) || 0
      : 0;
    if (amountValue <= 0) {
      toast("Amount must be greater than 0", {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      return;
    }

    // Validate amount limits
    const MIN_AMOUNT = 100;
    const MAX_AMOUNT = 1000000;

    if (amountValue < MIN_AMOUNT) {
      toast(`Minimum amount is ₹${MIN_AMOUNT.toLocaleString("en-IN")}`, {
        type: "error",
        hideProgressBar: true,
        autoClose: 3000,
      });
      return;
    }

    if (amountValue > MAX_AMOUNT) {
      toast(`Maximum amount is ₹${MAX_AMOUNT.toLocaleString("en-IN")}`, {
        type: "error",
        hideProgressBar: true,
        autoClose: 3000,
      });
      return;
    }

    if (bonusValue < 0) {
      toast("Bonus amount cannot be negative", {
        type: "error",
        hideProgressBar: true,
        autoClose: 3000,
      });
      return;
    }

    if (amountValue + bonusValue > MAX_AMOUNT) {
      toast(
        `Combined amount and bonus cannot exceed Rs ${MAX_AMOUNT.toLocaleString("en-IN")}`,
        {
          type: "error",
          hideProgressBar: true,
          autoClose: 4000,
        },
      );
      return;
    }

    // Balance validation for debit transactions
    if (WALLET_BALANCE_TRANSACTION_TYPES.includes(transactionType)) {
      if (amountValue > selectedUserBalance) {
        toast(
          `Insufficient balance! Available: ₹${selectedUserBalance.toLocaleString()}, Required: ₹${amountValue.toLocaleString()}`,
          {
            type: "error",
            hideProgressBar: true,
            autoClose: 4000,
          },
        );
        return;
      }
    }

    // Pass all fields including remarks and cashoutMode to backend
    dispatch(
      createAdminTransaction(
        selectedUser,
        transactionType,
        amountValue,
        selectedClub || null,
        remarks.trim(),
        // Top-ups are always taken as paid: the wallet is credited immediately
        transactionType === "deposit" ? "paid" : null,
        transactionType === "cashout" ? cashoutMode : null,
        bonusValue,
      ),
    );
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="w-full h-full">
      <ScreenHeader
        title="Transactions"
        actions={
          <FilterToggle
            open={filtersOpen}
            onToggle={() => setFiltersOpen((o) => !o)}
            count={dateFrom || dateTo ? 1 : 0}
            controls="transaction-filters"
          />
        }
      >
        <Tabs
          className="mt-3"
          items={TRANSACTION_TABS}
          value={activeTab}
          onChange={setActiveTab}
          ariaLabel="Transaction view"
        />
      </ScreenHeader>

      <FilterCard
        id="transaction-filters"
        className="mt-4"
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        chips={dateFrom || dateTo ? [{ icon: CalendarDays, label: `${dateFrom || '…'} → ${dateTo || '…'}` }] : []}
        emptyLabel="All dates"
      >
        <div className="flex flex-wrap items-end gap-3">
          <TextInput
            label="From"
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <TextInput
            label="To"
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
          />
          {(dateFrom || dateTo) && (
            <UIButton
              variant="ghost"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
              }}
            >
              Clear dates
            </UIButton>
          )}
        </div>
      </FilterCard>

      {activeTab === "spend" ? (
        <>
          {/* Spend Summary Cards - click a category to filter the table */}
          <h2 className="text-lg font-semibold text-gray-700 mt-4 mb-2">
            Player Settlements
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-4">
            <div className="bg-white shadow-md rounded p-4 border-l-4 border-red-500">
              <p className="text-gray-500 text-sm">Total Settled</p>
              <p className="text-2xl font-bold text-red-600">
                ₹{spendTotal.toLocaleString("en-IN")}
              </p>
            </div>
            <button
              type="button"
              aria-pressed={unpaidOnly}
              title={unpaidOnly ? "Show all entries" : "Show only unpaid entries"}
              onClick={() => setUnpaidOnly(!unpaidOnly)}
              className={`bg-white shadow-md rounded p-4 border-l-4 text-left ${
                unpaidOnly ? "border-cm-gold ring-1 ring-cm-gold" : "border-cm-warning"
              }`}
            >
              <p className="text-gray-500 text-sm">Unpaid</p>
              <p className="text-2xl font-bold text-cm-warning">
                ₹{spendUnpaidTotal.toLocaleString("en-IN")}
              </p>
            </button>
            {SPEND_CATEGORIES.map((category) => (
              <button
                key={category.key}
                type="button"
                aria-pressed={spendCategory === category.key}
                onClick={() =>
                  setSpendCategory(
                    spendCategory === category.key ? null : category.key,
                  )
                }
                className={`bg-white shadow-md rounded p-4 border-l-4 text-left ${
                  spendCategory === category.key
                    ? "border-cm-gold ring-1 ring-cm-gold"
                    : "border-blue-500"
                }`}
              >
                <p className="text-gray-500 text-sm">{category.label}</p>
                <p className="text-2xl font-bold text-blue-600">
                  ₹{(spendTotals[category.key] || 0).toLocaleString("en-IN")}
                </p>
              </button>
            ))}
          </div>

          <MaterialTable
            /* Add Spend disabled for now
            toolbarActions={
              <button type="button" onClick={() => setOpenSpendModal(true)} title="Add Spend" aria-label="Add Spend" className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-red-100 text-red-600 transition-colors hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-400">
                <HandCoins size={19} strokeWidth={2.2} />
              </button>
            }
            */
            icons={tableIcons}
            columns={spendHeadCells}
            data={visibleSpendData}
            isLoading={spendLoading}
            options={{
              exportButton: false,
              search: true,
              rowStyle: {
                height: "5px",
                fontSize: 13,
              },
              paging: true,
              pageSize: 10,
              emptyRowsWhenPaging: false,
              pageSizeOptions: [10, 20, 50],
              headerStyle: {
                position: "sticky",
                top: "0",
              },
            }}
          />
        </>
      ) : (
        <>
      {/* Financial Summary Cards */}
      <h2 className="text-lg font-semibold text-gray-700 mt-4 mb-2">
        Financial Summary
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
        <div className="bg-white shadow-md rounded p-4 border-l-4 border-blue-500">
          <p className="text-gray-500 text-sm">Total Top-ups</p>
          <p className="text-2xl font-bold text-blue-600">
            ₹{financialSummary.totalDeposits?.toLocaleString() || 0}
          </p>
        </div>
      </div>

      <MaterialTable
        toolbarActions={
          <>
            <button type="button" onClick={handleExportExcel} title="Export to Excel" aria-label="Export to Excel" className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-inset ring-teal-200 transition-all hover:bg-teal-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-cm-gold">
              <FileSpreadsheet size={18} strokeWidth={2} />
            </button>
            <button type="button" onClick={handleExportPDF} title="Export to PDF" aria-label="Export to PDF" className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200 transition-all hover:bg-violet-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400">
              <FileDown size={18} strokeWidth={2} />
            </button>
            {canAddTransactions && (
              <button type="button" onClick={openAddTransactionModal} title="Add Transaction" aria-label="Add Transaction" className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-emerald-100 text-emerald-600 transition-colors hover:bg-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-400">
                <Plus size={19} strokeWidth={2.2} />
              </button>
            )}
          </>
        }
        icons={tableIcons}
        columns={headCells}
        data={data}
        options={{
          exportButton: false,
          search: true,
          exportAllData: false,
          padding: "dense",
          rowStyle: {
            height: "5px",
            fontSize: 13,
          },
          paging: true,
          pageSize: 10,
          emptyRowsWhenPaging: false,
          pageSizeOptions: [10, 20, 50],
          headerStyle: {
            position: "sticky",
            top: "0",
          },
          onValueChange: (value) => setFilteredData(value), // Capture filtered data
        }}
      />
        </>
      )}

      {/* ✅ Updated Add Transaction Modal */}
      {openModal && (
        <>
          {/* Modal Overlay */}
          <div className="fixed inset-0 z-40 bg-black opacity-25"></div>

          {/* Modal Container */}
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto min-h-screen">
            <div className="relative w-full max-w-lg mx-auto bg-white rounded-lg shadow-lg">
              {/* Modal Header with Balance in Top Right */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Add Transaction</h3>

                {/* Balance Display in Top Right */}
                {selectedUser && (
                  <div className="bg-blue-100 px-3 py-1 rounded-full">
                    <span className="text-xs text-blue-600 font-medium">
                      Balance:{" "}
                    </span>
                    <span className="text-sm text-blue-800 font-bold">
                      ₹{selectedUserBalance.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Modal Body */}
              <div className="px-4 py-4 max-h-96 overflow-y-auto">
                {/* User Selection */}
                <div className="w-full mb-4">
                  <Autocomplete
                    options={userOptions}
                    getOptionLabel={(option) => option.title}
                    value={userOptions.find((opt) => opt.id === selectedUser) || null}
                    onChange={(event, newValue) => handleUserSelection(newValue ? newValue.id : "")}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select User *"
                        variant="outlined"
                        size="small"
                        placeholder="Type to search player..."
                      />
                    )}
                    sx={playerSelectSx}
                  />
                </div>

                {isSingleClubStaff ? (
                  <div className="w-full mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Club
                    </label>
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-800">
                      {clubOptions.find(
                        (club) => String(club.id) === singleAssignedClubId,
                      )?.title ||
                        `${singleAssignedClub?.clubCode || ""}${singleAssignedClub?.clubCode && singleAssignedClub?.name ? " - " : ""}${singleAssignedClub?.name || "Assigned Club"}`}
                    </div>
                  </div>
                ) : showClubDropdown ? (
                  <Select
                    width="w-full mb-4"
                    name="Club *"
                    value={selectedClub}
                    onChange={(e) => setSelectedClub(e.target.value)}
                    options={clubOptions}
                  />
                ) : null}

                {/* Transaction Type — read-only when there is only one option */}
                {transactionTypeOptions.length === 1 ? (
                  <div className="w-full mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transaction Type
                    </label>
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-800">
                      {transactionTypeOptions[0].title}
                    </div>
                  </div>
                ) : (
                  <Select
                    width="w-full mb-4"
                    name="Transaction Type *"
                    value={transactionType}
                    onChange={handleTransactionTypeChange}
                    options={transactionTypeOptions}
                  />
                )}

                {/* Amount Input with Warning */}
                <div className="w-full mb-4">
                  <Input
                    width="w-full"
                    name="Amount *"
                    type="number"
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder="Enter amount"
                  />

                  {/* Transaction Limits Display */}
                  <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
                    <span className="font-medium">Allowed range:</span> ₹100 -
                    ₹10,00,000
                  </div>

                  {selectedUser &&
                    WALLET_BALANCE_TRANSACTION_TYPES.includes(
                      transactionType,
                    ) && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                        <span className="font-medium">Note:</span> Maximum
                        amount: ₹{selectedUserBalance.toLocaleString()}
                      </div>
                    )}

                </div>

                {BONUS_TRANSACTION_TYPES.includes(transactionType) && (
                  <div className="w-full mb-4 p-3 bg-violet-50 border border-violet-200 rounded">
                    <h4 className="font-medium text-sm text-gray-700 mb-2">
                      Bonus Amount
                    </h4>
                    <Input
                      width="w-full"
                      name="Bonus Amount"
                      type="number"
                      value={bonusAmount}
                      onChange={handleBonusAmountChange}
                      placeholder="Enter bonus amount"
                    />
                    <div className="mt-2 text-xs text-violet-700">
                      Bonus credits extra value to the wallet. Total wallet
                      credit: Rs{" "}
                      {(numericAmount + numericBonusAmount).toLocaleString(
                        "en-IN",
                      )}
                    </div>
                  </div>
                )}

                {/* Cashout Mode */}
                {transactionType === "cashout" && (
                  <div className="w-full mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                    <h4 className="font-medium text-sm text-gray-700 mb-3">
                      Cashout Mode *
                    </h4>
                    <div className="flex space-x-6">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="cashoutMode"
                          value="online"
                          checked={cashoutMode === "online"}
                          onChange={(e) => setCashoutMode(e.target.value)}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm font-medium text-blue-700">
                          Online
                        </span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="cashoutMode"
                          value="cash"
                          checked={cashoutMode === "cash"}
                          onChange={(e) => setCashoutMode(e.target.value)}
                          className="w-4 h-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                        />
                        <span className="ml-2 text-sm font-medium text-emerald-700">
                          Cash
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {/* ✅ Remarks Input (Mandatory) */}
                <div className="w-full mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Remarks *
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Enter remarks (mandatory)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    rows={2}
                  />
                </div>

                {/* Mandatory Fields Note */}
                <p className="text-xs text-gray-500 mt-2">
                  All the fields with * are mandatory
                </p>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end px-4 py-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeAddTransactionModal}
                  className="text-red-500 font-bold uppercase text-sm px-4 py-2 mr-2"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={addTransactionHandler}
                  disabled={loadingCreateAdminTransaction}
                  className={`${
                    loadingCreateAdminTransaction
                      ? "bg-gray-300"
                      : "bg-emerald-500 hover:bg-emerald-600"
                  } text-white font-bold uppercase text-sm px-6 py-2 rounded shadow`}
                >
                  {loadingCreateAdminTransaction
                    ? "Creating..."
                    : "Add Transaction"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add Spend Modal - disabled for now (button commented out above) */}
      {
      // {openSpendModal && (
      //   <>
      //     {/* Modal Overlay */}
      //     <div className="fixed inset-0 z-40 bg-black opacity-25"></div>
      //
      //     {/* Modal Container */}
      //     <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto min-h-screen">
      //       <div className="relative w-full max-w-lg mx-auto bg-white rounded-lg shadow-lg">
      //         {/* Modal Header with Balance in Top Right */}
      //         <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
      //           <h3 className="text-lg font-semibold">Add Spend</h3>
      //
      //           {selectedUser && (
      //             <div className="bg-blue-100 px-3 py-1 rounded-full">
      //               <span className="text-xs text-blue-600 font-medium">
      //                 Balance:{" "}
      //               </span>
      //               <span className="text-sm text-blue-800 font-bold">
      //                 ₹{selectedUserBalance.toLocaleString()}
      //               </span>
      //             </div>
      //           )}
      //         </div>
      //
      //         {/* Modal Body */}
      //         <div className="px-4 py-4 max-h-96 overflow-y-auto">
      //           {/* Player Selection */}
      //           <div className="w-full mb-4">
      //             <Autocomplete
      //               options={userOptions}
      //               getOptionLabel={(option) => option.title}
      //               value={userOptions.find((opt) => opt.id === selectedUser) || null}
      //               onChange={(event, newValue) => handleUserSelection(newValue ? newValue.id : "")}
      //               isOptionEqualToValue={(option, value) => option.id === value?.id}
      //               renderInput={(params) => (
      //                 <TextField
      //                   {...params}
      //                   label="Select Player *"
      //                   variant="outlined"
      //                   size="small"
      //                   placeholder="Type to search player..."
      //                 />
      //               )}
      //               sx={playerSelectSx}
      //             />
      //           </div>
      //
      //           {isSingleClubStaff ? (
      //             <div className="w-full mb-4">
      //               <label className="block text-sm font-medium text-gray-700 mb-1">
      //                 Club
      //               </label>
      //               <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-800">
      //                 {clubOptions.find(
      //                   (club) => String(club.id) === singleAssignedClubId,
      //                 )?.title ||
      //                   `${singleAssignedClub?.clubCode || ""}${singleAssignedClub?.clubCode && singleAssignedClub?.name ? " - " : ""}${singleAssignedClub?.name || "Assigned Club"}`}
      //               </div>
      //             </div>
      //           ) : showClubDropdown ? (
      //             <Select
      //               width="w-full mb-4"
      //               name="Club *"
      //               value={selectedClub}
      //               onChange={(e) => setSelectedClub(e.target.value)}
      //               options={clubOptions}
      //             />
      //           ) : null}
      //
      //           {/* Current table time of the player at this club */}
      //           {selectedUser && selectedClub && (
      //             <div className="w-full mb-4 rounded-md border border-cm-border-strong bg-cm-surface-2 p-3">
      //               <div className="flex items-center justify-between mb-2">
      //                 <span className="text-sm font-semibold text-cm-text">Table time</span>
      //                 {tableTime && (
      //                   <span className="text-xs text-cm-muted">
      //                     {formatMinutes(tableTime.totalMinutes)} · Due{" "}
      //                     <span className="font-bold text-cm-gold-text">
      //                       ₹{tableTime.totalDue.toLocaleString("en-IN")}
      //                     </span>
      //                   </span>
      //                 )}
      //               </div>
      //               {tableTimeLoading ? (
      //                 <p className="text-xs text-cm-muted m-0">Loading table time...</p>
      //               ) : !tableTime || tableTime.stays.length === 0 ? (
      //                 <p className="text-xs text-cm-muted m-0">
      //                   Not seated at a table and no unpaid table bills at this club.
      //                 </p>
      //               ) : (
      //                 <ul className="flex flex-col gap-1.5 m-0 p-0 list-none">
      //                   {tableTime.stays.map((stay) => (
      //                     <li key={stay._id} className="flex items-start justify-between gap-3 text-xs">
      //                       <div className="min-w-0">
      //                         <div className="text-cm-text">
      //                           {stay.table?.tableName || "Table"}
      //                           {stay.table?.tableNumber ? ` (${stay.table.tableNumber})` : ""} · Seat {stay.seatNumber}
      //                         </div>
      //                         <div className="text-cm-muted">
      //                           {stay.status === "playing" ? "Playing now" : "Unpaid bill"} ·{" "}
      //                           {formatMinutes(stay.durationMinutes)} · ₹{stay.hourlyRate}/hr
      //                           {stay.prepaidAmount > 0 &&
      //                             ` · ₹${stay.prepaidAmount.toLocaleString("en-IN")} paid`}
      //                         </div>
      //                       </div>
      //                       <span className="shrink-0 font-semibold text-cm-text tabular-nums">
      //                         ₹{stay.due.toLocaleString("en-IN")}
      //                       </span>
      //                     </li>
      //                   ))}
      //                 </ul>
      //               )}
      //               {tableTime?.totalDue > 0 && !hasTableTime && (
      //                 <button
      //                   type="button"
      //                   onClick={() =>
      //                     handleSpendTypesChange(null, [
      //                       ...spendTypes,
      //                       SPEND_CATEGORIES.find((category) => category.key === TABLE_TIME_KEY),
      //                     ])
      //                   }
      //                   className="mt-2 w-full rounded-md border border-cm-gold px-3 py-1.5 text-xs font-semibold text-cm-gold-text transition-colors hover:bg-cm-gold-soft focus:outline-none focus:ring-2 focus:ring-cm-gold"
      //                 >
      //                   Add ₹{tableTime.totalDue.toLocaleString("en-IN")} table time to this spend
      //                 </button>
      //               )}
      //             </div>
      //           )}
      //
      //           {/* Spend Types - one amount field per selected type */}
      //           <div className="w-full mb-4">
      //             <Autocomplete
      //               multiple
      //               disableCloseOnSelect
      //               options={SPEND_CATEGORIES}
      //               getOptionLabel={(option) => option.label}
      //               value={spendTypes}
      //               onChange={handleSpendTypesChange}
      //               isOptionEqualToValue={(option, value) => option.key === value?.key}
      //               renderInput={(params) => (
      //                 <TextField
      //                   {...params}
      //                   label="Select Spend Type *"
      //                   variant="outlined"
      //                   size="small"
      //                   placeholder={spendTypes.length ? "" : "Select spend types..."}
      //                 />
      //               )}
      //               sx={playerSelectSx}
      //             />
      //           </div>
      //
      //           {spendItems.map((item) => (
      //             <div key={item.category.key} className="w-full mb-3">
      //               <label
      //                 htmlFor={`spend-amount-${item.category.key}`}
      //                 className="flex justify-between text-sm font-medium text-gray-700 mb-1"
      //               >
      //                 <span>{item.category.label} amount *</span>
      //                 {item.category.key === TABLE_TIME_KEY && tableTime?.totalDue > 0 && (
      //                   <span className="text-xs text-cm-muted">
      //                     Due ₹{tableTime.totalDue.toLocaleString("en-IN")}
      //                   </span>
      //                 )}
      //               </label>
      //               <Input
      //                 id={`spend-amount-${item.category.key}`}
      //                 width="w-full"
      //                 name={`${item.category.label} amount`}
      //                 type="number"
      //                 min="0"
      //                 value={item.amount}
      //                 onChange={(e) =>
      //                   setSpendAmounts((amounts) => ({
      //                     ...amounts,
      //                     [item.category.key]: e.target.value,
      //                   }))
      //                 }
      //                 placeholder="Enter amount"
      //               />
      //             </div>
      //           ))}
      //
      //           {/* Total spend */}
      //           {spendItems.length > 0 && (
      //             <div className="w-full mb-4 rounded-md border border-cm-border-strong bg-cm-surface-2 p-3 text-sm">
      //               <div className="flex justify-between font-bold text-cm-text">
      //                 <span>Total spend</span>
      //                 <span className="tabular-nums">
      //                   ₹{spendTotalAmount.toLocaleString("en-IN")}
      //                 </span>
      //               </div>
      //               {selectedUser && (
      //                 <div className="mt-1 flex justify-between text-xs text-cm-muted">
      //                   <span>Wallet balance after</span>
      //                   <span
      //                     className={`tabular-nums font-semibold ${
      //                       spendBalanceAfter < 0 ? "text-cm-danger" : ""
      //                     }`}
      //                   >
      //                     ₹{spendBalanceAfter.toLocaleString("en-IN")}
      //                   </span>
      //                 </div>
      //               )}
      //               {selectedUser && !walletCoversSpend && (
      //                 <p className="mt-2 mb-0 rounded bg-cm-danger-soft px-2 py-1.5 text-xs text-cm-danger">
      //                   Wallet balance is ₹{spendShortfall.toLocaleString("en-IN")} short. Collect cash,
      //                   add cash to the wallet, or add the spend as unpaid.
      //                 </p>
      //               )}
      //             </div>
      //           )}
      //
      //           {/* Payment */}
      //           {spendItems.length > 0 && selectedUser && (
      //             <div className="w-full mb-4">
      //               <p className="text-sm font-medium text-gray-700 mb-2">Payment *</p>
      //               <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Payment">
      //                 {SPEND_PAYMENT_OPTIONS.map((option) => {
      //                   const selected = paymentMode === option.value;
      //                   return (
      //                     <button
      //                       key={option.value}
      //                       type="button"
      //                       role="radio"
      //                       aria-checked={selected}
      //                       disabled={option.disabled}
      //                       onClick={() => setSpendPaymentMode(option.value)}
      //                       className={`rounded-md border px-3 py-2 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-cm-gold ${
      //                         selected
      //                           ? "border-cm-gold bg-cm-gold-soft"
      //                           : "border-cm-border-strong bg-cm-surface-2 hover:border-cm-muted"
      //                       } ${option.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      //                     >
      //                       <span className={`block text-sm font-semibold ${selected ? "text-cm-gold-text" : "text-cm-text"}`}>
      //                         {option.label}
      //                       </span>
      //                       <span className="block text-xs text-cm-muted">{option.help}</span>
      //                     </button>
      //                   );
      //                 })}
      //               </div>
      //
      //               {paymentMode === "deposit" && (
      //                 <div className="mt-3">
      //                   <label
      //                     htmlFor="spend-deposit-amount"
      //                     className="flex justify-between text-sm font-medium text-gray-700 mb-1"
      //                   >
      //                     <span>Cash to add to wallet *</span>
      //                     {spendShortfall > 0 && (
      //                       <span className="text-xs text-cm-muted">
      //                         At least ₹{spendShortfall.toLocaleString("en-IN")}
      //                       </span>
      //                     )}
      //                   </label>
      //                   <Input
      //                     id="spend-deposit-amount"
      //                     width="w-full"
      //                     name="Cash to add to wallet"
      //                     type="number"
      //                     min="0"
      //                     value={spendDepositAmount === "" ? (spendShortfall || "") : spendDepositAmount}
      //                     onChange={(e) => setSpendDepositAmount(e.target.value)}
      //                     placeholder="Enter cash amount"
      //                   />
      //                 </div>
      //               )}
      //             </div>
      //           )}
      //
      //           {/* Remarks (optional) */}
      //           <div className="w-full mb-4">
      //             <label className="block text-sm font-medium text-gray-700 mb-1">
      //               Remarks
      //             </label>
      //             <textarea
      //               value={spendRemarks}
      //               onChange={(e) => setSpendRemarks(e.target.value)}
      //               placeholder="Enter remarks (optional)"
      //               className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
      //               rows={2}
      //             />
      //           </div>
      //
      //           <p className="text-xs text-gray-500 mt-2">
      //             All the fields with * are mandatory
      //           </p>
      //         </div>
      //
      //         {/* Modal Footer */}
      //         <div className="flex justify-end px-4 py-2 border-t border-gray-200">
      //           <button
      //             type="button"
      //             onClick={closeSpendModal}
      //             disabled={savingSpend}
      //             className="text-red-500 font-bold uppercase text-sm px-4 py-2 mr-2"
      //           >
      //             Close
      //           </button>
      //           <button
      //             type="button"
      //             onClick={addSpendHandler}
      //             disabled={savingSpend}
      //             className={`${
      //               savingSpend
      //                 ? "bg-gray-300"
      //                 : "bg-emerald-500 hover:bg-emerald-600"
      //             } text-white font-bold uppercase text-sm px-6 py-2 rounded shadow`}
      //           >
      //             {savingSpend
      //               ? "Saving..."
      //               : selectedUser && spendItems.length > 0
      //                 ? SPEND_PAYMENT_OPTIONS.find((option) => option.value === paymentMode)?.action
      //                 : "Add Spend"}
      //           </button>
      //         </div>
      //       </div>
      //     </div>
      //   </>
      // )}
      }

      <SpendBillSettleDialog
        open={Boolean(settlePlayer)}
        onClose={() => setSettlePlayer(null)}
        player={settlePlayer}
        token={currentUser?.token}
        onSettled={fetchSpend}
      />
      <TableBillSettleDialog
        open={Boolean(settleBillsPlayer)}
        onClose={() => setSettleBillsPlayer(null)}
        player={settleBillsPlayer}
        token={currentUser?.token}
        onSettled={fetchSpend}
      />
      <SpendInvoiceDialog
        open={Boolean(invoiceTransactions)}
        onClose={() => setInvoiceTransactions(null)}
        transactions={invoiceTransactions}
        labelOf={spendLabelOf}
        token={currentUser?.token}
      />
    </div>
  );
};

export default AdminTransactions;
